import React, { FunctionComponent, memo } from 'react'
import styles from './UserTasks.module.sass'
import UserPanel from '../elements/UserElement/UserElement'
import { User } from '../../../responses/users'
import List from '../lists/List/List'
import { Tender, TenderStatus } from '../pickers/TenderPicker/TenderPicker'
import PriorityPicker from '../pickers/PriorityPicker/PriorityPicker'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { urlWithParams } from '../../../http/utils'
import { ROUTES } from '../../../http/routes'
import { useAnyPermissions, useRootDispatch, useRootSelector } from '../../../utils/hooks'
import { objectSelector, projectSelector, tasksSelector } from '../../../utils/selectors'
import Color from '../pickers/ColorPicker/Color'
import { taskActions } from '../../../redux/data/task/task-actions'
import { PERMISSIONS } from '../../../constants/permissions'
import Spacer from '../../etc/Spacer'
import ObjectName from '../../etc/ObjectName/ObjectName'
import Ellipsis from '../../etc/Ellipsis/Ellipsis'
import { Classes, Colors } from '../../../styles'
import { Task } from '../../../responses/tasks'
import SideMark from '../../etc/SideMark/SideMark'
import DeadlinePicker from '../date/DeadlinePicker'
import { getId } from '../../../utils/entity-utils'
import * as A from 'fp-ts/Array'
import * as O from 'fp-ts/Ord'
import { RootState } from '../../../redux/root'
import { ID } from '../../../types/UtilityTypes'
import { ordStringOpt } from '../../../utils/utils'

interface Props {
    className?: string,
    user?: User | null,
    unread?: boolean,
    onClick?: () => void,
}

export const TaskLine: FunctionComponent<{task?: Task}> = props => {
    const object = useRootSelector(objectSelector(props.task?.project_work_object_id))
    const project = useRootSelector(projectSelector(object?.project_id))

    return <Link
        className={styles.link}
        to={urlWithParams({ taskId: props.task?.id })(ROUTES.TASK)}
    >
        {
            props.task?.type.short_name ?
                <Color color={props.task.type.color}>{props.task.type.short_name}</Color> :
                <div>{props.task?.type.name}</div>
        }
        <div className={Classes.f13b}>{project?.code}</div>
        <div className={styles.description}>
            <Ellipsis className={clsx(Classes.f13b, Classes.cDarkGrey)}>
                <span>{project?.brand.title}</span>&emsp;<span>{project?.short_name}</span>
            </Ellipsis>
            <ObjectName className={Classes.f11} object={object}/>
        </div>
        <Spacer/>
        {props.children}
    </Link>
}

const Line: FunctionComponent<{ task: Task, unread: boolean }> = memo(props => {
    const dispatch = useRootDispatch()
    const unread = useRootSelector(state => state.global.notification.tasks[props.task.id])

    const canOpen = useAnyPermissions([PERMISSIONS.TASK.OPEN])

    return <>
        {(props.unread && unread > 0) && <SideMark color={Colors.orange}/>}
        <TaskLine task={props.task}/>
        <Tender state={props.task.tender_id ?? TenderStatus.CANCELED}/>
        <DeadlinePicker
            value={props.task.deadline_at}
            deadline_text={props.task.deadline}
            deadline_type={props.task.deadline_type}
            onChange={deadline_at => dispatch(taskActions.update.request({
                id: props.task.id,
                deadline_at
            }))}
        />
        <PriorityPicker
            className={styles.priority}
            priority={props.task.priority_id ?? 1}
            disabled={!canOpen}
            onChange={priority_id => dispatch(taskActions.update.request({
                id: props.task.id,
                priority_id,
            }))}/>
    </>
})

Line.displayName = 'UserTasksLine'

const ordNumber = O.contramap((task: Task) => task.project_code)(ordStringOpt)
const ordPriority = O.contramap((task: Task) => task.priority_id)(O.getDualOrd(O.ordNumber))

const sortedTasksSelector = (userId?: ID) => (state: RootState) => {
    const direction = state.global.user.data.settings.tasks_direction
    const taskIds = userId && state.data.task.byUser[userId] || []
    const tasks = tasksSelector(taskIds)(state)
    return A.sort(direction === 'by_number'? ordNumber: ordPriority)(tasks)
}

const UserTasks: FunctionComponent<Props> = props => {
    const tasks = useRootSelector(sortedTasksSelector(props.user?.id))
    return <div className={clsx(props.className, styles.userTasks)} onClick={props.onClick}>
        <UserPanel
            withoutRequest
            className={styles.userPanel}
            user={props.user}
        />
        <List
            className={styles.list}
            data={tasks}
            toKey={getId}
            toClassName={() => styles.task}
            render={task => <Line task={task} unread={!!props.unread}/>}
        />
    </div>
}

export default UserTasks